import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function TemplateGallery() {
  const { t } = useTranslation();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [isMobile, setIsMobile] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Initialize client-side rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Check if device is mobile
  useEffect(() => {
    if (!isClient || typeof window === 'undefined') return;

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isClient]);

  // Template data with more variety
  const templates = [
    { id: 1, name: "Classic Elegance", category: "Traditional", color: "bg-red-50" },
    { id: 2, name: "Modern Minimalist", category: "Contemporary", color: "bg-blue-50" },
    { id: 3, name: "Royal Heritage", category: "Traditional", color: "bg-purple-50" },
    { id: 4, name: "Clean Professional", category: "Modern", color: "bg-gray-50" },
    { id: 5, name: "Floral Delight", category: "Decorative", color: "bg-pink-50" },
    { id: 6, name: "Golden Touch", category: "Luxury", color: "bg-yellow-50" },
    { id: 7, name: "Ocean Breeze", category: "Contemporary", color: "bg-cyan-50" },
    { id: 8, name: "Vintage Charm", category: "Traditional", color: "bg-amber-50" },
  ];

  // Get unique categories
  const categories = ["All", ...new Set(templates.map(template => template.category))];

  // Filter templates based on active category
  const filteredTemplates = activeCategory === "All" 
    ? templates 
    : templates.filter(template => template.category === activeCategory);

  // Limit templates on mobile (show only first 4)
  // During SSR, show all templates. On client, limit based on screen size
  const displayTemplates = (isClient && isMobile) 
    ? filteredTemplates.slice(0, 4)
    : filteredTemplates;

  return (
    <section 
      className="py-20 bg-white"
      aria-label="Template Gallery"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 abc-bold">
            Beautifully Handcrafted Biodata Templates
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto abc-regular mb-8">
            We understand your needs, and we're here to help you create a biodata that truly reflects your personality. 
            Choose from our wide range of beautifully designed templates.
          </p>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                  activeCategory === category
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } abc-semibold`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Template Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {displayTemplates.map((template, index) => (
            <div
              key={template.id}
              className={`group relative cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl rounded-xl overflow-hidden ${template.color} ${
                selectedTemplate === template.id ? 'ring-4 ring-pink-500' : ''
              }`}
              onClick={() => setSelectedTemplate(template.id)}
              role="button"
              tabIndex={0}
              aria-label={`Select ${template.name} template`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setSelectedTemplate(template.id);
                }
              }}
            >
              {/* Template Preview */}
              <div className="relative">
                <img
                  src={`https://d16akdef0hveqs.cloudfront.net/bgs/b${template.id}.png`}
                  width={280}
                  height={396}
                  alt={`${template.name} biodata template`}
                  className="w-full h-auto transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full p-3">
                      <svg className="w-6 h-6 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Selection Indicator */}
                {selectedTemplate === template.id && (
                  <div className="absolute top-4 right-4 bg-pink-500 text-white rounded-full p-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Template Info */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1 abc-semibold">{template.name}</h3>
                <p className="text-sm text-gray-600 abc-regular">{template.category}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA for Template Selection */}
        <div className="text-center">
          <button
            onClick={() => {
              if (typeof window !== 'undefined') {
                const element = document.getElementById("create-biodata");
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }
            }}
            className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-purple-300 abc-semibold"
            aria-label="Start Creating Your Biodata"
          >
            <span className="mr-2">🎨</span>
            Start Creating Your Biodata
            <span className="ml-2">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}
